// Libraries
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, Box, ListItem, Paper, TextField, Typography } from "@mui/material";

// Icons
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";

// Layouts
import { CustomPopper } from "@/Layouts/components/CustomPopper";
import ChipWithTooltip from "@/Layouts/components/Chip/ChipWithTooltip";

// Helpers
import { maxHeight } from "@/Common/helpers/visuals";
import { getDisplayValues, getOptions } from "./helpers";
// Types
import { TSelectedValuesInput, IHierarchicalOption } from "@/Layouts/types/HierarchicalSelector";

type THierarchicalSelectorProps = {
	value: TSelectedValuesInput[];
	disabled: boolean;
	disableClearable?: boolean;
	options: IHierarchicalOption<any>[];
	mapFunction?: (item: any) => { name: string; id: string };
	className?: {
		autocomplete?: string; // Tailwind classes
		textField?: string; // Tailwind classes
	};
	amountOfItems?: number;
	onChange: (newValue?: TSelectedValuesInput) => void;
};

const defaultTailwindClasses = [
	"!min-w-full",
	"[&_.MuiInputBase-root]:!pr-[30px] [&_.MuiInputBase-root]:!pl-[1px]",
	"[&_.MuiAutocomplete-tag]:!mr-0 [&_.MuiAutocomplete-tag]:!ml-[2px]",
];

export default function HierarchicalSelector(props: Readonly<THierarchicalSelectorProps>) {
	const {
		value: input = [],
		disabled,
		options = [],
		mapFunction,
		className,
		disableClearable = false,
		amountOfItems = 7,
		onChange,
	} = props;

	const [open, setOpen] = useState<boolean>(false);

	const inputRef = useRef<HTMLInputElement | null>(null);

	// Helper to check if a value has children in the hierarchical structure
	const hasChildren = useCallback(
		(value: string, currentPath: TSelectedValuesInput[]): boolean => {
			let currentOptions = options;

			// Navigate to the current level
			for (const pathItem of currentPath) {
				const selectedOption = currentOptions.find((opt) => opt.displayLabel === pathItem.value);
				if (selectedOption?.items) {
					currentOptions = selectedOption.items as IHierarchicalOption<any>[];
				} else {
					return false;
				}
			}

			// Check if the value has children at this level
			const option = currentOptions.find((opt) => opt.displayLabel === value);
			return Boolean(option && option.items && option.items.length > 0);
		},
		[options]
	);

	const autocompleteOptions = useMemo<TSelectedValuesInput[]>(() => {
		const navigationPath = input.filter((item) => item.label === item.value);
		if (navigationPath.length === 0) {
			// At root level - show top-level categories

			return getDisplayValues(options);
		} else {
			// Navigate through the hierarchy dynamically using getOptions recursively
			let currentOptions = options;

			// Walk through each level of the navigation path
			for (const pathItem of navigationPath) {
				const nextLevelOptions = getOptions(currentOptions, pathItem.value, mapFunction);

				if (nextLevelOptions.length === 0) {
					return [];
				}

				// If this is the last item in our navigation path, return these options
				if (pathItem === navigationPath[navigationPath.length - 1]) {
					return nextLevelOptions;
				}

				// Otherwise, find the selected option and continue deeper
				const selectedOption = currentOptions.find((opt) => opt.displayLabel === pathItem.value);
				if (selectedOption?.items) {
					currentOptions = selectedOption.items as IHierarchicalOption<any>[];
				} else {
					return [];
				}
			}
			return [];
		}
	}, [input, options, mapFunction]);

	const hasOptions = autocompleteOptions.length > 0;

	useEffect(() => {
		if (hasOptions && input?.length) {
			setOpen(true);
			inputRef.current?.focus();
		}
	}, [input]);

	return (
		<Paper elevation={0} sx={{ width: "100%", backgroundColor: "transparent" }}>
			<Box sx={{ display: "flex", alignItems: "center", columnGap: "0.3rem" }}>
				<Autocomplete
					multiple
					disabled={disabled}
					options={autocompleteOptions}
					value={input}
					disableCloseOnSelect
					disablePortal
					open={open && hasOptions}
					onOpen={() => setOpen(true)}
					onClose={() => setOpen(false)}
					PopperComponent={CustomPopper}
					popupIcon={<ArrowForwardIosRoundedIcon className="p-1 rotate-90" />}
					forcePopupIcon={hasOptions}
					getOptionLabel={(option) => option.value}
					isOptionEqualToValue={() => true}
					className={clsx({
						[defaultTailwindClasses.join(" ")]: true,
						"[&_.Mui-disabled]:!p-0": disabled,
						[className?.autocomplete ?? ""]: Boolean(className?.autocomplete),
					})}
					onChange={(_, newValue, reason) => {
						if (reason === "clear") {
							onChange();
						} else if (reason === "removeOption") {
							inputRef.current?.focus();
							onChange(newValue?.[newValue.length - 1]);
						} else if (reason === "selectOption") {
							const selectedOption = newValue?.[newValue.length - 1];
							onChange(selectedOption);
						}
					}}
					renderInput={(params) => {
						return (
							<TextField
								{...params}
								inputRef={inputRef}
								component="li"
								type={"button"}
								className={clsx({
									[className?.textField ?? ""]: Boolean(className?.textField),
									"advance-select-open": open,
									"advance-select-expand-area": disableClearable,
									"[&_.MuiInputBase-input]:!hidden": !hasOptions,
									"[&_.MuiInputBase-root]:!bg-light-error [&_.MuiInputBase-root]:!rounded-[7px]": hasOptions,
									"[&_.MuiOutlinedInput-notchedOutline]:!border-red-500 [&_.MuiOutlinedInput-notchedOutline]:!border-1":
										hasOptions,
								})}
							/>
						);
					}}
					renderTags={(value, getTagProps) => {
						return value.map((option, index) => (
							<ChipWithTooltip
								key={index}
								index={index}
								option={option}
								value={value}
								getTagProps={getTagProps}
								width={{
									default: "auto",
									max: "120px", //150
								}}
							/>
						));
					}}
					renderOption={({ key: _, ...props }, option) => {
						return (
							<ListItem
								{...props}
								key={option.value}
								sx={{
									backgroundColor: "white !important",
									display: "flex !important",
									alignItems: "center !important",
									justifyContent: "space-between !important",
									"&:hover": {
										backgroundColor: "var(--gray-background) !important",
									},
								}}
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									onChange(option);
								}}
							>
								<Typography className="!font-sans !text-[14px] !font-normal !text-default">{option.label}</Typography>
								{hasChildren(option.value, input) && <ArrowForwardIosIcon className="!text-[12px] !text-default" />}
							</ListItem>
						);
					}}
					ListboxProps={{
						className: "advanced-select-dropdown",
						sx: { maxHeight: maxHeight(amountOfItems) },
					}}
				/>
			</Box>
		</Paper>
	);
}
